/**
 * @file ui/shared/motion-tokens.ts
 *
 * Framer Motion spring presets derived from the official MD3 Expressive
 * motion physics system (May 2025).
 *
 * Usage hierarchy:
 *   1. Import a named preset (e.g. `FAST_SPATIAL_SPRING`) for convenience.
 *   2. Call `getMotionSpring(speed, type)` when you need to pick dynamically.
 *   3. Call `getMotionTransitionCSS(speed, type, scheme?)` for pure-CSS contexts.
 *
 * @see https://m3.material.io/styles/motion/overview/how-it-works
 * @see https://m3.material.io/styles/motion/overview/specs
 */

import type { Transition } from "motion/react";

// ─────────────────────────────────────────────────────────────────────────────
// MD3 Spring Token Structure
//
// MD3 defines springs as dampingRatio (0–1) + stiffness.
// Framer Motion uses absolute `damping` coefficient.
//
// Conversion (with Framer's default mass = 1):
//   damping = 2 × dampingRatio × √stiffness
//
// Computed values (rounded to 2 decimal places):
//
//   fast  spatial:  2 × 0.6 × √800   = 2 × 0.6 × 28.28  ≈ 33.94
//   fast  effects:  2 × 1.0 × √3800  = 2 × 61.64         ≈ 123.29
//   default spatial: 2 × 0.8 × √380  = 2 × 0.8 × 19.49  ≈ 31.19
//   default effects: 2 × 1.0 × √1600 = 2 × 40            = 80
//   slow  spatial:  2 × 0.8 × √200   = 2 × 0.8 × 14.14  ≈ 22.63
//   slow  effects:  2 × 1.0 × √800   = 2 × 28.28         ≈ 56.57
// ─────────────────────────────────────────────────────────────────────────────

// ── Spatial springs — use for position, size, border-radius, rotation, scale ─

/**
 * MD3 Expressive — Fast Spatial Spring
 * `md.sys.motion.spring.fast.spatial` (dampingRatio: 0.6, stiffness: 800)
 *
 * Use for: small components (buttons, switches, chips).
 * Has notable bounce/overshoot — feels snappy and expressive.
 */
export const FAST_SPATIAL_SPRING: Transition = {
	type: "spring",
	stiffness: 800,
	damping: 33.94,
} as const;

/**
 * MD3 Expressive — Default Spatial Spring
 * `md.sys.motion.spring.default.spatial` (dampingRatio: 0.8, stiffness: 380)
 *
 * Use for: medium elements (bottom sheets, menus, navigation rail).
 * Gentle overshoot — the go-to spring for most UI animations.
 */
export const DEFAULT_SPATIAL_SPRING: Transition = {
	type: "spring",
	stiffness: 380,
	damping: 31.19,
} as const;

/**
 * MD3 Expressive — Slow Spatial Spring
 * `md.sys.motion.spring.slow.spatial` (dampingRatio: 0.8, stiffness: 200)
 *
 * Use for: full-screen transitions and large layout shifts.
 * Subtle, slow overshoot.
 */
export const SLOW_SPATIAL_SPRING: Transition = {
	type: "spring",
	stiffness: 200,
	damping: 22.63,
} as const;

// ── Effects springs — use for color, opacity (critically damped, no bounce) ──

/**
 * MD3 Expressive — Fast Effects Spring
 * `md.sys.motion.spring.fast.effects` (dampingRatio: 1.0, stiffness: 3800)
 *
 * Use for: color changes on switches, icon swaps, badge counts.
 * Critically damped — very fast, no overshoot.
 */
export const FAST_EFFECTS_SPRING: Transition = {
	type: "spring",
	stiffness: 3800,
	damping: 123.29,
} as const;

/**
 * MD3 Expressive — Default Effects Spring
 * `md.sys.motion.spring.default.effects` (dampingRatio: 1.0, stiffness: 1600)
 *
 * Use for: opacity fades, color transitions for medium-sized elements.
 * Critically damped — smooth with no overshoot.
 */
export const DEFAULT_EFFECTS_SPRING: Transition = {
	type: "spring",
	stiffness: 1600,
	damping: 80,
} as const;

/**
 * MD3 Expressive — Slow Effects Spring
 * `md.sys.motion.spring.slow.effects` (dampingRatio: 1.0, stiffness: 800)
 *
 * Use for: full-screen opacity/color transitions.
 * Critically damped — slow and smooth.
 */
export const SLOW_EFFECTS_SPRING: Transition = {
	type: "spring",
	stiffness: 800,
	damping: 56.57,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Dynamic helper
// ─────────────────────────────────────────────────────────────────────────────

export type SpringSpeed = "fast" | "default" | "slow";
export type SpringAnimationType = "spatial" | "effects";

const springMap = {
	fast: { spatial: FAST_SPATIAL_SPRING, effects: FAST_EFFECTS_SPRING },
	default: { spatial: DEFAULT_SPATIAL_SPRING, effects: DEFAULT_EFFECTS_SPRING },
	slow: { spatial: SLOW_SPATIAL_SPRING, effects: SLOW_EFFECTS_SPRING },
} as const;

/**
 * Pick a Framer Motion spring by speed and type.
 *
 * @example
 * ```tsx
 * transition={getMotionSpring("fast", "spatial")}
 * ```
 */
export function getMotionSpring(
	speed: SpringSpeed,
	type: SpringAnimationType,
): Transition {
	return springMap[speed][type];
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS transition strings
// For contexts where Framer Motion is not used (plain CSS / Tailwind).
// Uses official Web spring → CSS cubic-bezier conversion table.
// ─────────────────────────────────────────────────────────────────────────────

const CSS_CURVES = {
	expressive: {
		fast: {
			spatial: {
				curve: "cubic-bezier(0.42, 1.67, 0.21, 0.90)",
				durationMs: 350,
			},
			effects: {
				curve: "cubic-bezier(0.31, 0.94, 0.34, 1.00)",
				durationMs: 150,
			},
		},
		default: {
			spatial: {
				curve: "cubic-bezier(0.38, 1.21, 0.22, 1.00)",
				durationMs: 500,
			},
			effects: {
				curve: "cubic-bezier(0.34, 0.80, 0.34, 1.00)",
				durationMs: 200,
			},
		},
		slow: {
			spatial: {
				curve: "cubic-bezier(0.39, 1.29, 0.35, 0.98)",
				durationMs: 650,
			},
			effects: {
				curve: "cubic-bezier(0.34, 0.88, 0.34, 1.00)",
				durationMs: 300,
			},
		},
	},
	standard: {
		fast: {
			spatial: {
				curve: "cubic-bezier(0.27, 1.06, 0.18, 1.00)",
				durationMs: 350,
			},
			effects: {
				curve: "cubic-bezier(0.31, 0.94, 0.34, 1.00)",
				durationMs: 150,
			},
		},
		default: {
			spatial: {
				curve: "cubic-bezier(0.27, 1.06, 0.18, 1.00)",
				durationMs: 500,
			},
			effects: {
				curve: "cubic-bezier(0.34, 0.80, 0.34, 1.00)",
				durationMs: 200,
			},
		},
		slow: {
			spatial: {
				curve: "cubic-bezier(0.27, 1.06, 0.18, 1.00)",
				durationMs: 750,
			},
			effects: {
				curve: "cubic-bezier(0.34, 0.88, 0.34, 1.00)",
				durationMs: 300,
			},
		},
	},
} as const;

export type MotionScheme = "expressive" | "standard";

/**
 * Returns a CSS `transition` value string using the official MD3 web curve
 * conversion, e.g. `"500ms cubic-bezier(0.38, 1.21, 0.22, 1.00)"`.
 *
 * @example
 * ```ts
 * element.style.transition = `border-radius ${getMotionTransitionCSS("default", "spatial")}`;
 * ```
 */
export function getMotionTransitionCSS(
	speed: SpringSpeed,
	type: SpringAnimationType,
	scheme: MotionScheme = "expressive",
): string {
	const { curve, durationMs } = CSS_CURVES[scheme][speed][type];
	return `${durationMs}ms ${curve}`;
}
